import React, { useState, useMemo } from 'react';
import EmptyState from '../design-system/EmptyState';
import { Skull, Swords, TrendingUp, Clock, Zap, Brain, Map } from 'lucide-react';
import DeathsSection from './analytics/DeathsSection';
import MatchupsSection from './analytics/MatchupsSection';
import TrendsSection from './analytics/TrendsSection';
import SessionsSection from './analytics/SessionsSection';
import DamageSection from './analytics/DamageSection';
import HeatmapSection from './analytics/HeatmapSection';
import LaneComparisonSection from './analytics/LaneComparisonSection';

const SECTIONS = [
  { id: 'deaths', label: 'Morts', icon: Skull, desc: 'Quand et pourquoi tu meurs' },
  { id: 'heatmap', label: 'Heatmap', icon: Map, desc: 'Map des morts géolocalisées' },
  { id: 'matchups', label: 'Matchups', icon: Swords, desc: 'Performance par adversaire/allié' },
  { id: 'lane', label: 'Lane vs ADC', icon: Swords, desc: 'Comparaison face à l\'ADC adverse' },
  { id: 'trends', label: 'Progression', icon: TrendingUp, desc: 'Évolution dans le bloc' },
  { id: 'damage', label: 'Damage', icon: Zap, desc: 'Profil damage' },
];

export default function AnalyticsTab({ games, sessions, rankSnapshots, userSettings, champions, version }) {
  const [section, setSection] = useState('deaths');

  if (games.length === 0) return <EmptyState title="Pas encore de données à analyser" body="Enregistre quelques games pour voir tes analytics" size="lg" />;

  return (
    <div className="space-y-6">
      {/* Sous-navigation */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
        {SECTIONS.map(s => {
          const Icon = s.icon;
          const isActive = section === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              className={`relative border p-3 text-left transition group overflow-hidden ${
                isActive
                  ? 'border-cyan-400 bg-gradient-to-br from-cyan-400/10 to-fuchsia-400/10'
                  : 'border-zinc-800 hover:border-zinc-700 bg-zinc-950'
              }`}
            >
              {isActive && <div className="absolute top-0 right-0 w-16 h-16 bg-cyan-400/10 rounded-full blur-2xl" />}
              <div className="relative">
                <Icon size={14} className={isActive ? 'text-cyan-400' : 'text-zinc-500'} />
                <div className={`font-bold text-sm mt-1 ${isActive ? 'text-white' : 'text-zinc-300'}`}>{s.label}</div>
                <div className="text-[10px] text-zinc-500 mt-0.5 leading-tight">{s.desc}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div>
        {section === 'deaths' && <DeathsSection games={games} />}
        {section === 'heatmap' && <HeatmapSection games={games} champions={champions} />}
        {section === 'matchups' && <MatchupsSection games={games} champions={champions} version={version} userSettings={userSettings} />}
        {section === 'lane' && <LaneComparisonSection games={games} champions={champions} version={version} userSettings={userSettings} />}
        {section === 'trends' && <TrendsSection games={games} />}
        {section === 'damage' && <DamageSection games={games} champions={champions} version={version} />}
      </div>
    </div>
  );
}
